import { Component, effect, input, model, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NominatimLocation, ViaCEPResponse } from '../../../shared/models/address.model';
import { sanitizeFields, validateBlank, watchFormRecursively } from '../../../shared/utils/form-utils';

import { AddressService } from './../../../services/address/address.service';
import { ButtonModule } from 'primeng/button';
import { Coordinate } from '../../../shared/models/geolocation.model';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FormFieldFeedbackPipe } from '../../../pipes/form-field-feedback/form-field-feedback.pipe';
import { InputMaskModule } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { Location } from '../../../shared/models/location.model';
import { MessageService } from 'primeng/api';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-location-form',
  imports: [
    InputText,
    ButtonModule,
    TextareaModule,
    InputMaskModule,
    ReactiveFormsModule,
    FormFieldFeedbackPipe,
    TooltipModule,
    FloatLabelModule
  ],
  templateUrl: './location-form.component.html',
  styleUrl: './location-form.component.scss'
})
export class LocationFormComponent {
  readonly locationForm = new FormGroup({
    finderPhone: new FormControl<string>('', {
      validators: [
        Validators.pattern(/^\(\d{2}\) \d{5}-\d{4}$/)
      ]
    }),
    detail: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    medication: new FormControl<string>(''),
    address: new FormGroup({
      street: new FormControl<string>('', {
        validators: [
          Validators.required,
          validateBlank()
        ]
      }),
      number: new FormControl<string>('', {
        validators: [
          Validators.required,
          validateBlank()
        ]
      }),
      neighborhood: new FormControl<string>('', {
        validators: [
          Validators.required,
          validateBlank()
        ]
      }),
      zipCode: new FormControl<string>('', {
        validators: [
          Validators.required,
          validateBlank()
        ]
      }),
      complement: new FormControl<string>(''),
      city: new FormControl<string>('', {
        validators: [
          Validators.required,
          validateBlank()
        ]
      }),
      state: new FormControl<string>('', {
        validators: [
          Validators.required,
          validateBlank()
        ]
      })
    })
  });

  readonly coords = input<Coordinate | null>(null);
  readonly initialData = input<Location | null>(null);
  readonly onSubmit = output<any>();
  readonly enabled = model<boolean>(true);
  readonly isLoading = model.required<boolean>();

  readonly id = uuidv4();

  readonly formWatched = watchFormRecursively(this.locationForm);

  constructor(private readonly addressService: AddressService,
    private readonly messageService: MessageService) {
    effect(() => {
      if (this.enabled()) {
        this.locationForm.enable();
      } else {
        this.locationForm.disable();
      }
    });

    effect(() => {
      if (this.coords()) {
        this.onFetchAddressFromCoords();
      }
    })

    effect(() => {
      if (this.initialData()) {
        this.enabled.set(false);
        this.locationForm.patchValue(this.initialData()!);
      } else {
        this.enabled.set(true);
        this.locationForm.reset();
      }
    });
  }

  onFetchAddressFromCoords(): void {
    this.isLoading.set(true);
    this.locationForm.disable();

    this.addressService.fetchAddressByCoordinates(this.coords()!)
      .pipe(finalize(() => {
        this.isLoading.set(false);
        this.locationForm.enable();
      }))
      .subscribe({
        next: (addressData: NominatimLocation) => {
          if (!addressData.address) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Atenção',
              detail: 'Não foi possível recuperar um endereço para sua localização!',
              life: 4000
            });
            return;
          }

          this.locationForm.patchValue({
            address: {
              street: addressData.address.road ?? '',
              neighborhood: addressData.address.suburb ?? addressData.address.neighbourhood ?? '',
              city: addressData.address.city ?? addressData.address.municipality ?? '',
              state: addressData.address.state ?? addressData.address.state_district ?? '',
              complement: addressData.address.residential ?? addressData.address.county ?? '',
              zipCode: addressData.address.postcode ?? ''
            }
          });
        }
      });
  }

  onFetchAndFillFromZipCode(): void {
    const zipCode = this.locationForm.get('address.zipCode')?.value;
    if (!zipCode) {
      return;
    }

    this.isLoading.set(true);
    this.locationForm.disable();

    this.addressService.fetchAddressByZipCode(zipCode)
      .pipe(finalize(() => {
        this.isLoading.set(false);
        this.locationForm.enable();
      }))
      .subscribe({
        next: (addressData: ViaCEPResponse) => {
          if (addressData.erro) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Atenção',
              detail: 'CEP não encontrado! Verifique o CEP informado.',
              life: 4000
            });
            return;
          }

          this.locationForm.patchValue({
            address: {
              street: addressData.logradouro,
              neighborhood: addressData.bairro,
              city: addressData.localidade,
              state: addressData.uf,
              complement: addressData.complemento
            }
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao recuperar o endereço! CEP inválido ou serviço indisponível.',
            life: 4000
          });
        }
      });
  }

  submitForm(): void {
    this.isLoading.set(true);

    if (this.locationForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todos os campos corretamente!',
        life: 4000
      });

      this.isLoading.set(false);
      return;
    }

    sanitizeFields(this.locationForm);
    this.onSubmit.emit({
      ...this.locationForm.value,
      latitude: this.coords()?.latitude.toString(),
      longitude: this.coords()?.longitude.toString()
    } as Location);
  }
}
